import { ESRI_ATTRIBUTION, OSM_ATTRIBUTION } from '../config/mapLayers';

/**
 * MapAttribution Component
 *
 * Displays proper attribution for the currently active map layer.
 * Required for compliance with Esri and OpenStreetMap terms of use.
 *
 * @param {Object} props - Component props
 * @param {string} props.activeLayer - Currently active layer ('satellite' | 'hybrid' | 'streets')
 * @param {string} [props.position='bottomright'] - Position of attribution control
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {JSX.Element} Attribution component
 */
export default function MapAttribution({
  activeLayer,
  position = 'bottomright',
  className = ''
}) {
  /**
   * Get the appropriate attribution text based on active layer
   * @returns {string} Attribution HTML string
   */
  const getAttribution = () => {
    if (activeLayer === 'satellite' || activeLayer === 'hybrid') {
      return ESRI_ATTRIBUTION;
    }
    return OSM_ATTRIBUTION;
  };

  /**
   * Get CSS position classes based on position prop
   * @returns {string} Tailwind CSS position classes
   */
  const getPositionClasses = () => {
    const positionMap = {
      bottomright: 'bottom-4 right-4',
      bottomleft: 'bottom-4 left-4',
      topright: 'top-4 right-4',
      topleft: 'top-4 left-4'
    };
    return positionMap[position] || positionMap.bottomright;
  };

  return (
    <div
      className={`absolute ${getPositionClasses()} ${className}`}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        padding: '4px 8px',
        fontSize: '0.75rem',
        lineHeight: '1.2',
        zIndex: 400,
        pointerEvents: 'none',
        maxWidth: '300px',
        borderRadius: '4px'
      }}
      role="contentinfo"
      aria-label="Map attribution"
    >
      <span
        dangerouslySetInnerHTML={{
          __html: getAttribution()
        }}
      />
    </div>
  );
}
